/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useLayoutEffect, useRef, ReactNode, useEffect } from "react";
import { useState } from "react";
import { EVENTS, IFloatingWindow, WindowDimensions } from "FloatingWindow/IFloatingWindowConstants";
import { overlayEvents } from "overlays/OverlayEvents";
import { convertTargetPointStringToNumber } from "utils/floatingWindowUtils";
import FloatingWindowsManagerInstance from "FloatingWindow/FloatingWindowsManager";

const FloatingWindowContext = React.createContext(undefined);

export const FloatingWindowProvider = ({ children, id, getPagesViewElementHandle, docViewRects }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [floatingWindowInstance, setFloatingWindowInstance] = useState(undefined);
  const [margin, setMargin] = useState(0);
  const [showCloseBtn, setShowCloseBtn] = useState(true);
  const [triggerAnimation, setTriggerAnimation] = useState("");
  const moveClientCbRef = useRef(() => {});
  const [className, setClassName] = useState(undefined);
  const [css, setCSS] = useState({});
  const [isDraggable, setIsDraggable] = useState(false);
  const [Component, setComponent] = useState(null);
  const pagesViewScrollHandler = useRef(null);
  const windowDimensionsRef = useRef({ width: 0, height: 0 });
  const [windowDimensions, setWindowDimensions] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const instance = FloatingWindowsManagerInstance.getWindowInstance(id);
    if (!instance) {
      return;
    }
    setFloatingWindowInstance(instance);
    setMargin(instance.margin);
    setComponent(instance.component);
    setIsDraggable(instance.draggableState);
    instance.on(EVENTS.TOGGLE_DRAGGABLE, val => {
      setIsDraggable(val);
    });
    instance.on(EVENTS.TOGGLE_CLOSE_BTN, val => {
      setShowCloseBtn(val);
    });
    instance.on(EVENTS.UPDATE_COMPONENT, component => {
      setComponent(component);
    });
    instance.on(EVENTS.SHOW, () => {
      setIsVisible(true);
    });
    instance.on(EVENTS.HIDE, () => {
      setIsVisible(false);
    });
    instance.on(EVENTS.MOVE, () => {
      moveClientCbRef.current();
    });
    instance.on(EVENTS.ANIMATION, animationId => {
      setTriggerAnimation(animationId);
      setTimeout(() => {
        setTriggerAnimation("");
      }, 1000);
    });
    instance.on(EVENTS.CLASS_NAME_OVERRIDE, className => {
      setClassName(className);
    });
    instance.on(EVENTS.CSS_OVERRIDE, css => {
      setCSS({ ...css });
    });
    instance.on(EVENTS.TOGGLE_SCROLL, val => {
      if (val) {
        if (pagesViewScrollHandler.current) {
          overlayEvents.unregister("pagesViewScrolled", pagesViewScrollHandler.current);
          pagesViewScrollHandler.current = null;
        }
        const pageViewHandle = getPagesViewElementHandle();
        const initalScrollTopOffset = pageViewHandle.scrollTop;
        const initalScrollLeftOffset = pageViewHandle.scrollLeft;

        let initialPositionForBaseScroll = null;
        if (instance.targetRect) {
          initialPositionForBaseScroll = Object.assign({}, instance.targetRect); // create a copy of the targetRect as we only need the initial value
        } else if (instance.targetPoint) {
          initialPositionForBaseScroll = Object.assign(
            {},
            convertTargetPointStringToNumber(docViewRects, instance.targetPoint, windowDimensionsRef.current)
          ); // create a copy of the targetPoint as we only need the initial value
        }
        const handler = evt => {
          const resultantScrollLeftOffset = evt.target.scrollLeft - initalScrollLeftOffset;
          const resultantScrollTopOffset = evt.target.scrollTop - initalScrollTopOffset;
          if (initialPositionForBaseScroll) {
            instance.moveTo(
              initialPositionForBaseScroll.x - resultantScrollLeftOffset,
              initialPositionForBaseScroll.y - resultantScrollTopOffset
            );
          }
        };

        pagesViewScrollHandler.current = handler;

        overlayEvents.on("pagesViewScrolled", handler);
      } else {
        if (pagesViewScrollHandler.current) {
          overlayEvents.unregister("pagesViewScrolled", pagesViewScrollHandler.current);
          pagesViewScrollHandler.current = null;
        }
      }
    });

    return () => {
      if (!instance) return;
      instance.removeListener(EVENTS.SHOW);
      instance.removeListener(EVENTS.HIDE);
      instance.removeListener(EVENTS.MOVE);
      instance.removeListener(EVENTS.ANIMATION);
      instance.removeListener(EVENTS.CLASS_NAME_OVERRIDE);
      instance.removeListener(EVENTS.CSS_OVERRIDE);
      instance.removeListener(EVENTS.TOGGLE_SCROLL);

      if (pagesViewScrollHandler.current) {
        overlayEvents.unregister("pagesViewScrolled", pagesViewScrollHandler.current);
        pagesViewScrollHandler.current = null;
      }
    };
  }, [id]);

  useEffect(() => {
    const instance = FloatingWindowsManagerInstance.getWindowInstance(id);
    if (!instance) return;

    if (instance.scrollState) {
      windowDimensionsRef.current = windowDimensions;
      instance.toggleScroll(() => true); // to reset base scroll point
    }
  }, [windowDimensions, id]);

  return (
    <FloatingWindowContext.Provider
      value={{
        isVisible,
        floatingWindowInstance,
        margin,
        moveClientCbRef,
        triggerAnimation,
        cssOverride: css,
        classNameOverride: className,
        Component,
        isDraggable,
        showCloseBtn,
        setWindowDimensions
      }}
    >
      {isVisible ? children : null}
    </FloatingWindowContext.Provider>
  );
};

export const useFloatingWindow = () => {
  const context = React.useContext(FloatingWindowContext);
  if (context === undefined) {
    throw new Error("useFloatingWindow must be used within a FloatingWindowProvider");
  }
  return context;
};
